import { __GetContentTypeConfigNew } from "@/utils/getters/gettersV2";
import {
  isAuthor,
  isCurrentEditor,
  isCurrentModerator,
} from "./userIdentityCheckHelpers";
import { __CheckAccessToContentType } from "@/utils/accessControl";

export const checkPermissionsToModifyDoc = (doc, user) => {
  const {
    meta: { kp_content_type: contentType, kp_contributed_by },
    kp_published_status: status,
    kp_moderator,
    currentEditor,
  } = doc;

  const userId = user?._id;
  const segment = __GetContentTypeConfigNew(contentType).general.segment;
  const isStaticPagesOrCollections = ["staticPages", "collections"].includes(
    segment
  );

  const BLOCK_authorCurrentlyEditingDoc =
    status === "editPublished" && !isCurrentEditor(currentEditor, user);
  const BLOCK_moderatorCurrentlyEditingDoc =
    status === "underModeration" && !isCurrentModerator(kp_moderator, user);

  // EDIT ACCESS LOGIC
  // For staticPages/collections segments: User needs UPDATE permission
  // For other segments: User must be the author AND have UPDATE permission
  // Additional conditions: Document status must be published, editPublished, or sentBack
  // If editPublished: User must be the current editor
  const canEdit = () => {
    // Check if status allows editing
    const statusAllowsEdit = [
      "published",
      "editPublished",
      "sentBack",
    ].includes(status);
    if (!statusAllowsEdit) return false;

    // If editPublished: User must be the current editor
    if (BLOCK_authorCurrentlyEditingDoc) return false;

    // For staticPages/collections segments: User needs UPDATE permission
    if (isStaticPagesOrCollections) {
      return __CheckAccessToContentType(user, "UPDATE", contentType);
    }

    // For other segments, i.e publishing: User must be the author AND have UPDATE permission
    const isUserAuthor = isAuthor(kp_contributed_by, user);
    const hasUpdatePermission = __CheckAccessToContentType(
      user,
      "UPDATE",
      contentType
    );
    return isUserAuthor && hasUpdatePermission;
  };

  // MODERATE ACCESS LOGIC
  // User must NOT be the author
  // User needs MODERATE permission
  // Additional conditions: Document status must be awaitingModeration or published
  // If underModeration: User must be the current moderator
  const canModerate = () => {
    // Static pages/collections don't have moderation
    if (isStaticPagesOrCollections) return false;

    // User must NOT be the author
    const isUserAuthor = isAuthor(kp_contributed_by, user);
    if (isUserAuthor) return false;

    // User needs MODERATE permission
    const hasModeratePermission = __CheckAccessToContentType(
      user,
      "MODERATE",
      contentType
    );
    if (!hasModeratePermission) return false;

    // If underModeration: User must be the current moderator
    if (BLOCK_moderatorCurrentlyEditingDoc) return false;

    // Additional conditions: Document status must be awaitingModeration or published or underModeration
    return ["awaitingModeration", "published", "underModeration"].includes(
      status
    );
  };

  // DELETE ACCESS LOGIC
  // For staticPages/collections segments: User needs DELETE permission
  // For other segments: User must be the author AND have DELETE permission
  // Additional conditions: Document status must be published, editPublished, or sentBack
  // If editPublished: User must be the current editor
  const canDelete = () => {
    // If editPublished: User must be the current editor
    if (BLOCK_authorCurrentlyEditingDoc) return false;

    // Check if status allows deletion
    const statusAllowsDelete = [
      "published",
      "editPublished",
      "sentBack",
    ].includes(status);
    if (!statusAllowsDelete) return false;

    // For staticPages/collections segments: User needs DELETE permission
    if (isStaticPagesOrCollections) {
      return __CheckAccessToContentType(user, "DELETE", contentType);
    }

    // For other segments, i.e publishing: User must be the author AND have DELETE permission
    const isUserAuthor = isAuthor(kp_contributed_by, user);
    const hasDeletePermission = __CheckAccessToContentType(
      user,
      "DELETE",
      contentType
    );
    return isUserAuthor && hasDeletePermission;
  };

  // MODERATOR DELETE ACCESS LOGIC
  // User must NOT be the author AND have MODERATE permission
  // If underModeration: User must be the current moderator
  const canModeratorDelete = () => {
    // Static pages/collections don't have moderator delete
    if (isStaticPagesOrCollections) return false;

    // User must NOT be the author AND have MODERATE permission
    const isUserAuthor = isAuthor(kp_contributed_by, user);
    const hasModeratePermission = __CheckAccessToContentType(
      user,
      "MODERATE",
      contentType
    );
    if (isUserAuthor || !hasModeratePermission) return false;

    // If underModeration: User must be the current moderator
    if (BLOCK_moderatorCurrentlyEditingDoc) return false;

    // For published, awaitingMod, and underModeration (if not blocked above), user can moderate if they have mod permission.
    // Which they do, if the code has reached this point.

    if (
      ["published", "awaitingModeration", "underModeration"].includes(status)
    ) {
      return true;
    }
  };

  return {
    canEdit: canEdit(),
    canModerate: canModerate(),
    canDelete: canDelete(),
    canModeratorDelete: canModeratorDelete(),

    // Blocking reasons for UI feedback
    blockedReasons: {
      authorCurrentlyEditingDoc: BLOCK_authorCurrentlyEditingDoc,
      moderatorCurrentlyEditingDoc: BLOCK_moderatorCurrentlyEditingDoc,
    },

    // Additional info for debugging
    info: {
      status,
      contentType,
      segment,
      isStaticPagesOrCollections,
      isAuthor: isAuthor(kp_contributed_by, user),
      isCurrentEditor: isCurrentEditor(currentEditor, user),
      isCurrentModerator: isCurrentModerator(kp_moderator, user),
    },
  };
};
